import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext.js';
import { MessageCircle, Send, Heart, Flag, Edit2, Trash2, Reply, ChevronDown, ChevronUp, Star, Smile } from 'lucide-react';
import { toast } from 'react-toastify';

const CommentSection = ({ targetType, targetId, showRating = false }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [rating, setRating] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [expandedReplies, setExpandedReplies] = useState(new Set());
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showReplyEmojiPicker, setShowReplyEmojiPicker] = useState(null);
  const textareaRef = useRef(null);
  const replyTextareaRefs = useRef({});
  const emojiPickerRef = useRef(null);

  const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://elderfit.onrender.com/api';

  useEffect(() => {
    fetchComments();
  }, [targetType, targetId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${apiBaseUrl}/comments?targetType=${targetType}&targetId=${targetId}&includeReplies=false`,
        {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch comments');
      }

      const data = await response.json();
      setComments(data.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const fetchReplies = async (commentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${apiBaseUrl}/comments/${commentId}/replies`,
        {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch replies');
      }

      const data = await response.json();
      return data.replies || [];
    } catch (error) {
      console.error('Error fetching replies:', error);
      return [];
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('handleSubmitComment called', { user, newComment, targetType, targetId });
    
    if (!user) {
      toast.error('Please log in to leave a comment');
      return;
    }

    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    if (!targetId) {
      toast.error('Unable to post comment: Content ID is missing');
      console.error('targetId is missing:', { targetType, targetId });
      return;
    }

    if (!targetType) {
      toast.error('Unable to post comment: Content type is missing');
      console.error('targetType is missing:', { targetType, targetId });
      return;
    }

    // Close emoji picker if open
    setShowEmojiPicker(false);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to leave a comment');
        return;
      }

      // Ensure targetId is a string
      const targetIdStr = String(targetId);
      
      const requestBody = {
        content: newComment.trim(),
        targetType: targetType,
        targetId: targetIdStr,
        ...(showRating && rating > 0 ? { rating: rating } : {})
      };

      console.log('Submitting comment:', requestBody);
      console.log('Target details:', { targetType, targetId, targetIdStr, targetIdType: typeof targetId });

      const response = await fetch(`${apiBaseUrl}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to post comment';
        try {
          const error = await response.json();
          console.error('Error response:', error);
          
          // Handle validation errors
          if (error.errors && Array.isArray(error.errors)) {
            errorMessage = error.errors.map(err => err.msg || err.message).join(', ');
          } else if (error.error) {
            errorMessage = error.error;
          } else if (typeof error === 'string') {
            errorMessage = error;
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Comment posted successfully:', data);

      toast.success('Comment submitted! It will appear after moderation.');
      setNewComment('');
      setRating(0);
      setShowEmojiPicker(false);
      // Don't refresh comments immediately since it won't show until approved
      // await fetchComments();
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error(error.message || 'Failed to post comment');
    }
  };

  const handleReply = async (parentId) => {
    if (!user) {
      toast.error('Please log in to reply');
      return;
    }

    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseUrl}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: replyText.trim(),
          targetType,
          targetId,
          parentComment: parentId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to post reply');
      }

      toast.success('Reply posted!');
      setReplyText('');
      setReplyingTo(null);
      // Refresh replies for that comment
      toggleReplies(parentId, true);
    } catch (error) {
      console.error('Error posting reply:', error);
      toast.error('Failed to post reply');
    }
  };

  const handleLike = async (commentId) => {
    if (!user) {
      toast.error('Please log in to like comments');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseUrl}/comments/${commentId}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to like comment');
      }

      const data = await response.json();
      // Update comment in state
      setComments(prev => prev.map(comment => {
        if (comment._id === commentId) {
          return { ...comment, likeCount: data.likeCount, hasLiked: data.hasLiked };
        }
        return comment;
      }));
    } catch (error) {
      console.error('Error liking comment:', error);
      toast.error('Failed to like comment');
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseUrl}/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete comment');
      }

      toast.success('Comment deleted');
      fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const handleEdit = async (commentId) => {
    if (!editText.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseUrl}/comments/${commentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: editText.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update comment');
      }

      toast.success('Comment updated');
      setEditingId(null);
      setEditText('');
      fetchComments();
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
    }
  };

  const handleReport = async (commentId) => {
    if (!window.confirm('Report this comment as inappropriate?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseUrl}/comments/${commentId}/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reason: 'inappropriate'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to report comment');
      }

      toast.success('Comment reported. Thank you for keeping the community safe.');
    } catch (error) {
      console.error('Error reporting comment:', error);
      toast.error('Failed to report comment');
    }
  };

  const toggleReplies = async (commentId, forceOpen = false) => {
    const isExpanded = expandedReplies.has(commentId);
    
    if (forceOpen || !isExpanded) {
      // Fetch and show replies
      const replies = await fetchReplies(commentId);
      setComments(prev => prev.map(comment => {
        if (comment._id === commentId) {
          return { ...comment, replies: replies };
        }
        return comment;
      }));
      setExpandedReplies(prev => new Set([...prev, commentId]));
    } else {
      // Hide replies
      setComments(prev => prev.map(comment => {
        if (comment._id === commentId) {
          return { ...comment, replies: [] };
        }
        return comment;
      }));
      setExpandedReplies(prev => {
        const newSet = new Set(prev);
        newSet.delete(commentId);
        return newSet;
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getUserInitials = (user) => {
    if (!user) return 'U';
    // Show "ES" for ElderFit Secrets for admin users
    if (user.role === 'admin') return 'ES';
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  const getUserDisplayName = (user) => {
    if (!user) return 'User';
    // Show "ElderFit Secrets" for admin users
    if (user.role === 'admin') return 'ElderFit Secrets';
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User';
  };

  // Emoji categories
  const emojiCategories = {
    'Smileys & People': ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '😶‍🌫️', '😵', '😵‍💫', '🤯', '🤠', '🥳', '🥸', '😎', '🤓', '🧐', '😕', '😟', '🙁', '☹️', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈', '👿', '💀', '☠️', '💩', '🤡', '👹', '👺', '👻', '👽', '👾', '🤖', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿', '😾'],
    'Gestures & Body': ['👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💪', '🦾', '🦿', '🦵', '🦶', '👂', '🦻', '👃', '🧠', '🫀', '🫁', '🦷', '🦴', '👀', '👁️', '👅', '👄', '💋', '🩸'],
    'Activities & Sports': ['⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🥅', '⛳', '🏹', '🎣', '🤿', '🥊', '🥋', '🎽', '🛹', '🛷', '⛸️', '🥌', '🎿', '⛷️', '🏂', '🪂', '🏋️', '🤼', '🤸', '🤺', '🤾', '🤹', '🧘', '🏄', '🏊', '🚣', '🧗', '🚵', '🚴', '🏇', '🤽', '🏌️'],
    'Objects & Symbols': ['✅', '❌', '⭐', '🌟', '💫', '✨', '💯', '🔥', '💪', '🎉', '🎊', '🎈', '🎁', '🏆', '🥇', '🥈', '🥉', '🏅', '🎖️', '🎗️', '🎫', '🎟️', '🎪', '🤹', '🎭', '🩰', '🎨', '🎬', '🎤', '🎧', '🎼', '🎹', '🥁', '🎷', '🎺', '🎸', '🪗', '🎻', '🪕', '🎲', '♟️', '🎯', '🎳', '🎮', '🎰', '🧩', '🃏', '🀄', '🎴', '🎭', '🎪'],
    'Nature': ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐽', '🐸', '🐵', '🙈', '🙉', '🙊', '🐒', '🐔', '🐧', '🐦', '🐤', '🐣', '🐥', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🕷️', '🦂', '🐢', '🐍', '🦎', '🦖', '🦕', '🐙', '🦑', '🦐', '🦞', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆', '🦓', '🦍', '🦧', '🐘', '🦛', '🦏', '🐪', '🐫', '🦒', '🦘', '🦬', '🐃', '🐂', '🐄', '🐎', '🐖', '🐏', '🐑', '🦙', '🐐', '🦌', '🐕', '🐩', '🦮', '🐕‍🦺', '🐈', '🐈‍⬛', '🪶', '🐓', '🦃', '🦤', '🦚', '🦜', '🦢', '🦩', '🕊️', '🐇', '🦝', '🦨', '🦡', '🦫', '🦦', '🦥', '🐁', '🐀', '🐿️', '🦔', '🌱', '🌲', '🌳', '🌴', '🌵', '🌶️', '🌷', '🌸', '🌹', '🌺', '🌻', '🌼', '🌽', '🌾', '🌿', '☘️', '🍀', '🍁', '🍂', '🍃', '🍄', '🌰', '🪴'],
    'Food & Drink': ['🍇', '🍈', '🍉', '🍊', '🍋', '🍌', '🍍', '🥭', '🍎', '🍏', '🍐', '🍑', '🍒', '🍓', '🫐', '🥝', '🍅', '🫒', '🥥', '🥑', '🍆', '🥔', '🥕', '🌽', '🌶️', '🫑', '🥒', '🥬', '🥦', '🧄', '🧅', '🍄', '🥜', '🌰', '🍞', '🥐', '🥖', '🫓', '🥨', '🥯', '🥞', '🧇', '🧀', '🍖', '🍗', '🥩', '🥓', '🍔', '🍟', '🍕', '🌭', '🥪', '🌮', '🌯', '🫔', '🥙', '🧆', '🥚', '🍳', '🥘', '🍲', '🫕', '🥣', '🥗', '🍿', '🧈', '🧂', '🥫', '🍱', '🍘', '🍙', '🍚', '🍛', '🍜', '🍝', '🍠', '🍢', '🍣', '🍤', '🍥', '🥮', '🍡', '🥟', '🥠', '🥡', '🦀', '🦞', '🦐', '🦑', '🦪', '🍦', '🍧', '🍨', '🍩', '🍪', '🎂', '🍰', '🧁', '🍫', '🍬', '🍭', '🍮', '🍯', '🍼', '🥛', '☕', '🫖', '🍵', '🍶', '🍾', '🍷', '🍸', '🍹', '🍺', '🍻', '🥂', '🥃', '🥤', '🧋', '🧃', '🧉', '🧊']
  };

  // Insert emoji into text at cursor position
  const insertEmoji = (emoji, isReply = false, replyId = null) => {
    if (isReply && replyId) {
      const textarea = replyTextareaRefs.current[replyId];
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = replyText;
        const newText = text.substring(0, start) + emoji + text.substring(end);
        setReplyText(newText);
        // Restore cursor position after emoji
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + emoji.length, start + emoji.length);
        }, 0);
      }
    } else {
      const textarea = textareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = newComment;
        const newText = text.substring(0, start) + emoji + text.substring(end);
        setNewComment(newText);
        // Restore cursor position after emoji
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + emoji.length, start + emoji.length);
        }, 0);
      }
    }
    // Close emoji picker
    if (isReply) {
      setShowReplyEmojiPicker(null);
    } else {
      setShowEmojiPicker(false);
    }
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
        setShowReplyEmojiPicker(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-2 mb-6">
        <MessageCircle className="w-5 h-5 text-indigo-600" />
        <h3 className="text-xl font-bold text-gray-900">Comments ({comments.length})</h3>
      </div>

      {/* Comment Form */}
      {user ? (
        <form onSubmit={handleSubmitComment} className="mb-8 border-b border-gray-200 pb-6">
          {showRating && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`w-6 h-6 ${
                        star <= rating
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="relative">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => {
                    // Allow Enter+Shift for new line, Enter alone submits
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmitComment(e);
                    }
                  }}
                  placeholder="Write a comment..."
                  rows="3"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  maxLength={1000}
                />
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="absolute right-2 bottom-2 p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                  title="Add emoji"
                >
                  <Smile size={20} />
                </button>
              </div>
              <button
                type="submit"
                onClick={(e) => {
                  console.log('Post button clicked');
                  // Ensure form submits even if emoji picker is open
                  setShowEmojiPicker(false);
                }}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 self-start"
              >
                <Send size={18} />
                Post
              </button>
            </div>
            
            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div
                ref={emojiPickerRef}
                onClick={(e) => e.stopPropagation()}
                className="absolute z-50 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-full max-w-md max-h-80 overflow-y-auto"
              >
                <div className="space-y-4">
                  {Object.entries(emojiCategories).map(([category, emojis]) => (
                    <div key={category}>
                      <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase">{category}</h4>
                      <div className="flex flex-wrap gap-2">
                        {emojis.map((emoji, idx) => (
                          <button
                            key={`${category}-${idx}`}
                            type="button"
                            onClick={() => insertEmoji(emoji, false)}
                            className="text-2xl hover:scale-125 transition-transform p-1 rounded hover:bg-gray-100"
                            title={emoji}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">{newComment.length}/1000 characters</p>
        </form>
      ) : (
        <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
          <p className="text-gray-600">
            <a href="/login" className="text-indigo-600 hover:underline font-medium">
              Log in
            </a>
            {' '}to leave a comment
          </p>
        </div>
      )}

      {/* Comments List */}
      <div className="space-y-6">
        {comments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No comments yet. Be the first to comment!</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment._id} className="border-b border-gray-200 pb-4 last:border-0">
              <div className="flex gap-3">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${comment.user?.role === 'admin' ? 'bg-indigo-200' : 'bg-indigo-100'}`}>
                    <span className={`font-semibold text-sm ${comment.user?.role === 'admin' ? 'text-indigo-700' : 'text-indigo-600'}`}>
                      {getUserInitials(comment.user)}
                    </span>
                  </div>
                </div>

                {/* Comment Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-semibold ${comment.user?.role === 'admin' ? 'text-indigo-600' : 'text-gray-900'}`}>
                      {getUserDisplayName(comment.user)}
                    </span>
                    {comment.user?.role === 'admin' && (
                      <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">
                        Official
                      </span>
                    )}
                    {comment.rating && (
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= comment.rating
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                    <span className="text-sm text-gray-500">
                      {formatDate(comment.createdAt)}
                    </span>
                  </div>

                  {editingId === comment._id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        maxLength={1000}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(comment._id)}
                          className="px-4 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingId(null);
                            setEditText('');
                          }}
                          className="px-4 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-700 mb-2 whitespace-pre-wrap">{comment.content}</p>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-4 text-sm">
                    <button
                      onClick={() => handleLike(comment._id)}
                      className={`flex items-center gap-1 ${
                        comment.hasLiked ? 'text-red-600' : 'text-gray-600 hover:text-red-600'
                      } transition-colors`}
                    >
                      <Heart
                        size={16}
                        className={comment.hasLiked ? 'fill-current' : ''}
                      />
                      {comment.likeCount > 0 && <span>{comment.likeCount}</span>}
                    </button>

                    <button
                      onClick={() => {
                        setReplyingTo(replyingTo === comment._id ? null : comment._id);
                        setReplyText('');
                      }}
                      className="flex items-center gap-1 text-gray-600 hover:text-indigo-600 transition-colors"
                    >
                      <Reply size={16} />
                      Reply
                    </button>

                    {comment.user?._id === user?._id && (
                      <>
                        <button
                          onClick={() => {
                            setEditingId(comment._id);
                            setEditText(comment.content);
                          }}
                          className="flex items-center gap-1 text-gray-600 hover:text-indigo-600 transition-colors"
                        >
                          <Edit2 size={16} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(comment._id)}
                          className="flex items-center gap-1 text-gray-600 hover:text-red-600 transition-colors"
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </>
                    )}

                    {comment.user?._id !== user?._id && user && (
                      <button
                        onClick={() => handleReport(comment._id)}
                        className="flex items-center gap-1 text-gray-600 hover:text-red-600 transition-colors"
                      >
                        <Flag size={16} />
                        Report
                      </button>
                    )}

                    {comment.repliesCount > 0 && (
                      <button
                        onClick={() => toggleReplies(comment._id)}
                        className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
                      >
                        {expandedReplies.has(comment._id) ? (
                          <>
                            <ChevronUp size={16} />
                            Hide {comment.repliesCount} {comment.repliesCount === 1 ? 'reply' : 'replies'}
                          </>
                        ) : (
                          <>
                            <ChevronDown size={16} />
                            View {comment.repliesCount} {comment.repliesCount === 1 ? 'reply' : 'replies'}
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Reply Form */}
                  {replyingTo === comment._id && (
                    <div className="mt-4 pl-4 border-l-2 border-indigo-200">
                      <div className="relative">
                        <textarea
                          ref={(el) => {
                            if (el) replyTextareaRefs.current[comment._id] = el;
                          }}
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Write a reply..."
                          rows="2"
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-2"
                          maxLength={1000}
                        />
                        <button
                          type="button"
                          onClick={() => setShowReplyEmojiPicker(showReplyEmojiPicker === comment._id ? null : comment._id)}
                          className="absolute right-2 bottom-3 p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                          title="Add emoji"
                        >
                          <Smile size={18} />
                        </button>
                      </div>
                      
                      {/* Reply Emoji Picker */}
                      {showReplyEmojiPicker === comment._id && (
                        <div
                          className="mb-2 bg-white border border-gray-300 rounded-lg shadow-lg p-4 max-h-64 overflow-y-auto"
                        >
                          <div className="space-y-3">
                            {Object.entries(emojiCategories).map(([category, emojis]) => (
                              <div key={category}>
                                <h4 className="text-xs font-semibold text-gray-600 mb-1.5 uppercase">{category}</h4>
                                <div className="flex flex-wrap gap-1.5">
                                  {emojis.slice(0, 20).map((emoji, idx) => (
                                    <button
                                      key={`reply-${category}-${idx}`}
                                      type="button"
                                      onClick={() => insertEmoji(emoji, true, comment._id)}
                                      className="text-xl hover:scale-125 transition-transform p-1 rounded hover:bg-gray-100"
                                      title={emoji}
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleReply(comment._id)}
                          className="px-4 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                        >
                          Post Reply
                        </button>
                        <button
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyText('');
                            setShowReplyEmojiPicker(null);
                          }}
                          className="px-4 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Replies */}
                  {expandedReplies.has(comment._id) && comment.replies && comment.replies.length > 0 && (
                    <div className="mt-4 pl-4 border-l-2 border-gray-200 space-y-4">
                      {comment.replies.map((reply) => (
                        <div key={reply._id} className="flex gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${reply.user?.role === 'admin' ? 'bg-indigo-100' : 'bg-gray-100'}`}>
                            <span className={`font-semibold text-xs ${reply.user?.role === 'admin' ? 'text-indigo-600' : 'text-gray-600'}`}>
                              {getUserInitials(reply.user)}
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`font-semibold text-sm ${reply.user?.role === 'admin' ? 'text-indigo-600' : 'text-gray-900'}`}>
                                {getUserDisplayName(reply.user)}
                              </span>
                              {reply.user?.role === 'admin' && (
                                <span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">
                                  Official
                                </span>
                              )}
                              <span className="text-xs text-gray-500">
                                {formatDate(reply.createdAt)}
                              </span>
                            </div>
                            <p className="text-gray-700 text-sm mb-2">{reply.content}</p>
                            <div className="flex items-center gap-4 text-xs">
                              <button
                                onClick={() => handleLike(reply._id)}
                                className={`flex items-center gap-1 ${
                                  reply.hasLiked ? 'text-red-600' : 'text-gray-600'
                                }`}
                              >
                                <Heart
                                  size={14}
                                  className={reply.hasLiked ? 'fill-current' : ''}
                                />
                                {reply.likeCount > 0 && <span>{reply.likeCount}</span>}
                              </button>
                              {reply.user?._id === user?._id && (
                                <button
                                  onClick={() => handleDelete(reply._id)}
                                  className="text-gray-600 hover:text-red-600"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CommentSection;

